import { useCallback } from 'react';

import { useResetMarketplaceAcquisitions } from './useMarketplaceAcquisitionStore';
import { useSetPostExitOffer } from './usePostExitOfferStore';

/**
 * Drops every atom that belongs to the SIGNED-IN SESSION rather than to the
 * browser.
 *
 * Signing out does not reload the page — `AuthProvider.signOutAsync` clears the
 * query cache and navigates — so a module-level atom outlives the account that
 * wrote it and goes on answering for the next login in this tab.
 *
 * THE CHOKEPOINT: a new session-scoped atom joins this hook. Never reset one at
 * a call site; that is how the list drifts.
 *
 * Deliberately NOT here:
 * - `useDisplayCurrency`, `useSidebarSectionCollapse` — `atomWithStorage`
 *   browser preferences, meant to survive a sign-out.
 * - `useSocketStore` — `useSocketInitializer`'s effect already tears the socket
 *   down when the user goes away, and this hook cannot disconnect it.
 * - `useDismissedQuestionnairesStore` — keyed by submission id, and no two
 *   accounts share one, so a stale entry can never match.
 */
export const useResetSessionStores = () => {
  const resetMarketplaceAcquisitions = useResetMarketplaceAcquisitions();
  const setPostExitOffer = useSetPostExitOffer();

  return useCallback(() => {
    resetMarketplaceAcquisitions();
    setPostExitOffer(false);
  }, [resetMarketplaceAcquisitions, setPostExitOffer]);
};
